import React, { useContext, useRef, useState as classicUseState } from 'react';

import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import * as yup from 'yup';
import CloseImage from '@assets/svg/fat-close.svg';
import { useMutation, useQueryClient } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  ICustomField,
  ISelectableValuesApiModel,
} from '@models/CustomField.model';

import { updateCustomField } from '@services/queries/customField';
import {
  Close,
  Confirm,
  Error,
  Header as CreateHeader,
  Input,
  Label,
  Line,
  Textarea,
  Form,
} from '@pages/ConfigurationPage/Components/FieldsManagement/Components/AssetStatus/Components/CustomStatus/components/CreateEquipmentStatus/CreateEquipmentStatus.styles';
import { Title } from '@pages/ConfigurationPage/Components/FieldsManagement/Components/AssetStatus/Components/CustomStatus/CustomStatus.styles';
import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import PlusImage from '@assets/svg/plus-gray.svg';
import ReactTooltip from 'react-tooltip';
import { DeleteDialog, Toggle } from '@components';
import { NavigationContext } from '@layout/navigation/navigation';
import { useState } from '@hookstate/core';
import { UpdateContentError } from '@services/queries/customField/updateCustomFieldContent/updateCustomFieldContent';
import AppError from '@utils/AppError';
import { isAppErrAndStatus } from '@utils/isAppErrAndStatus';
import { ScrollArea } from '@mantine/core';
import {
  Overlay,
  Container,
} from '../CreateCustomField/CreateCustomField.styles';
import { CustomFieldDetailsLoading } from './loading/CustomFieldDetails.loading';

import {
  ButtonClick,
  ButtonPlus,
  DownImg,
  GridDiv,
  ImageClick,
  Plus,
  RowDiv,
  ShortClose,
  TextClick,
  Tooltip,
  YesNo,
} from './CustomFieldDetails.styles';

const css = `
        .tooltip {
            font-size: 12px;
            padding: 16px;
            color: #FFF;
            background: #161A30;
            width: 242px;
            opacity: 1;
        }
        `;
interface IEditUserDetails {
  open: boolean;
  onClose: () => void;
  customField: ICustomField;
  customFields: ICustomField[];
  isLoading?: boolean;
  contentLength?: number;
}

interface IFormData {
  name: string;
  description?: string;
  type: string;
  defaultValue?: string;
  showOnOnboard?: boolean;
  isUnique?: boolean;
  isSearchable?: boolean;
}

const options = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'url', label: 'Url' },
  { value: 'contact', label: 'Contact' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'description', label: 'Description' },
];

const CustomFieldDetails = ({
  open,
  onClose,
  customField,
  customFields,
  isLoading,
  contentLength,
}: IEditUserDetails) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const portalDiv = document.getElementById('portal');
  const { studioId } = useContext(NavigationContext);

  const [isSearchable, setIsSearchable] = classicUseState(
    customField.isSearchable,
  );
  const [type, setType] = classicUseState(customField.type);
  const [name, setName] = classicUseState(customField.name);
  const [selectValues, setSelectValues] = classicUseState<
    ISelectableValuesApiModel[]
  >(customField.selectableValuesObject);

  const [haveDefaultValue, setHaveDefaultValue] = classicUseState(
    !!customField.defaultValue,
  );
  const [showOnOnboard, setShowOnOnboard] = classicUseState(
    customField.showOnOnboard || false,
  );
  const [showPlus, setShowPlus] = classicUseState(true);
  const [newSelect, setNewSelect] = classicUseState('');
  const [lastSelectable, setLastSelectable] =
    classicUseState<ISelectableValuesApiModel>({
      id: '',
      customFieldId: customField.id,
      value: '',
    });
  const openDeleteDialog = useState<boolean>(false);

  const queryClient = useQueryClient();
  const updateCustomFieldMutationRef = useRef<
    null | typeof updateCustomFieldMutation
  >(null);

  const schema = yup.object().shape({
    name: yup.string().required(),
    type: yup.string(),
    description: yup.string(),
    defaultValue: yup.string(),
    showOnOnboard: yup.boolean(),
    isUnique: yup.boolean().test('valuesAreUnique', async (value, ctx) => {
      if (!updateCustomFieldMutationRef.current) return true;
      const { variables, error } = updateCustomFieldMutationRef.current;
      const isSameValue = value === variables?.data.isUnique;

      if (isAppErrAndStatus(error, 409) && isSameValue) {
        return ctx.createError(error);
      }
      return true;
    }),
    isSearchable: yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setError,
    control,
    reset,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      isUnique: customField.isUnique,
    },
    mode: 'onChange',
  });

  const filterSearchableField = customFields.filter(e => e.isSearchable);

  const filterSearchableFiltered = filterSearchableField.find(
    e => e.isSearchable,
  );

  const toggleDefaultStatus = () => {
    if (
      filterSearchableFiltered &&
      filterSearchableFiltered.id !== customField.id &&
      !isSearchable
    ) {
      openDeleteDialog.set(true);
    }
    return setIsSearchable(!isSearchable);
  };

  const updateExistingSearchableFieldMutation = useMutation(updateCustomField, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studios', studioId, 'customField']);
      setSelectValues([]);
      reset();
      onClose();
    },
  });
  const updateCustomFieldMutation = useMutation(updateCustomField, {
    onSuccess: () => {
      if (filterSearchableField && isSearchable) {
        updateExistingSearchableFieldMutation.mutate({
          customFieldId: filterSearchableFiltered?.id,
          data: {
            isSearchable: false,
          },
        });
      }
      queryClient.invalidateQueries(['studios', studioId, 'customField']);
      setSelectValues([]);
      reset();
      onClose();
    },
    onError: (e: UpdateContentError | AppError) => {
      if (isAppErrAndStatus(e, 409)) {
        setError('isUnique', { type: 'validate', message: e.message });
      }
    },
  });
  updateCustomFieldMutationRef.current = updateCustomFieldMutation;
  const onSubmit = (dataInput: IFormData) => {
    if (!updateCustomFieldMutation.isLoading) {
      updateCustomFieldMutation.mutate({
        customFieldId: customField.id,
        data: {
          name: dataInput.name,
          type,
          description: dataInput.description,
          defaultValue: dataInput.defaultValue,
          selectValues,
          showOnOnboard,
          isUnique: dataInput.isUnique,
          isSearchable,
        },
      });
    }
  };

  const cancel = () => {
    if (lastSelectable.value.length > 0) {
      setSelectValues([...selectValues, lastSelectable]);
    }
    setLastSelectable({
      id: '',
      customFieldId: customField.id,
      value: '',
    });
    setNewSelect('');
    setShowPlus(true);
  };

  const getNameIndex = customFields.findIndex(
    item => item.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
  );
  const isErrorName =
    customField.name !== name &&
    getNameIndex !== undefined &&
    getNameIndex > -1;
  const isUniqueError = errors.isUnique;
  const uniqueErrMessage = errors.isUnique?.message;

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <style>{css}</style>
            {isLoading ? (
              <CustomFieldDetailsLoading />
            ) : (
              <Form onSubmit={handleSubmit(onSubmit)} className="form-body">
                <CreateHeader>
                  <Title className="headTitle">{`Editing ${customField.name}`}</Title>
                  <Close onClick={onClose}>
                    <BlueIcon icon="cross" color="#8F95B2" />
                  </Close>
                </CreateHeader>
                <ScrollArea classNames={{ viewport: 'form-body_inputs' }}>
                  <Label className="no-margin-top">Name</Label>
                  <Input
                    placeholder="Choose a Custom Field Name"
                    {...register('name')}
                    isError={isErrorName}
                    defaultValue={customField.name}
                    onChange={(e: any) => {
                      setName(e.target.value);
                    }}
                  />
                  {isErrorName && (
                    <Error>
                      There is already a custom field with this name
                    </Error>
                  )}
                  <Label className="optional">Description (Optional)</Label>
                  <Textarea
                    defaultValue={customField.description || ''}
                    placeholder=""
                    {...register('description')}
                  />
                  <Label>Type</Label>
                  {contentLength && contentLength > 0 ? (
                    <>
                      <Tooltip
                        data-tip="You cannot edit a field type if there are equipment using this field"
                        data-for="tooltipPhone"
                      >
                        <Input
                          placeholder={
                            customField.type ? customField.type : 'Empty'
                          }
                          defaultValue={
                            customField.type ? customField.type : 'Empty'
                          }
                          disabled
                        />
                        <DownImg />
                      </Tooltip>
                      <ReactTooltip
                        className="tooltip"
                        id="tooltipPhone"
                        effect="solid"
                      />
                    </>
                  ) : (
                    <>
                      <Controller
                        control={control}
                        defaultValue={
                          customField.type ? customField.type : 'Empty'
                        }
                        name="type"
                        render={({ field: { onChange, value } }) => (
                          <CustomPicker
                            options={options}
                            value={options.find(c => c.value === value)}
                            onChange={(val: any) => {
                              onChange(val.value);
                              setType(val.value);
                            }}
                            placeholder="Select a field type"
                          />
                        )}
                      />
                    </>
                  )}
                  {type === 'select' && (
                    <>
                      <Label>Select Values</Label>
                      <GridDiv>
                        {selectValues.map(item => {
                          return (
                            <ButtonClick>
                              <TextClick
                                onClick={async () => {
                                  if (showPlus) {
                                    setLastSelectable(item);
                                    setNewSelect(item.value);
                                    await setShowPlus(false);
                                    const filteredValue = selectValues.filter(
                                      value => value.id !== item.id,
                                    );
                                    setSelectValues(filteredValue);
                                    if (inputRef && inputRef.current) {
                                      inputRef.current.focus();
                                    }
                                  } else {
                                    const filteredValue = selectValues.filter(
                                      value => value.id !== item.id,
                                    );
                                    const updatedSelectableValue =
                                      Object.assign(lastSelectable, {
                                        value: newSelect,
                                      });
                                    const newValues = [
                                      ...filteredValue,
                                      updatedSelectableValue,
                                    ];
                                    setNewSelect(item.value);
                                    if (lastSelectable.value.length > 0) {
                                      setSelectValues(newValues);
                                    }
                                  }
                                  if (inputRef && inputRef.current) {
                                    inputRef.current.focus();
                                  }
                                }}
                              >
                                {item.value}
                              </TextClick>
                              <ImageClick>
                                <ShortClose
                                  src={CloseImage}
                                  alt="close"
                                  onClick={() => {
                                    setSelectValues(
                                      selectValues.filter(
                                        value => value.id !== item.id,
                                      ),
                                    );
                                  }}
                                />
                              </ImageClick>
                            </ButtonClick>
                          );
                        })}
                        {showPlus ? (
                          <ButtonPlus
                            onClick={async () => {
                              await setShowPlus(false);
                              if (inputRef && inputRef.current) {
                                inputRef.current.focus();
                              }
                            }}
                          >
                            <Plus src={PlusImage} alt="plus" />
                          </ButtonPlus>
                        ) : (
                          <Input
                            placeholder="Add a new select value"
                            ref={inputRef}
                            onChange={(e: any) => {
                              setNewSelect(e.target.value);
                            }}
                            onBlur={cancel}
                            value={newSelect}
                            onKeyDown={(event: any) => {
                              if (
                                event.key === 'Enter' &&
                                newSelect.length > 0
                              ) {
                                setSelectValues([
                                  ...selectValues,
                                  {
                                    id: '',
                                    value: newSelect,
                                    customFieldId: customField.id,
                                  },
                                ]);
                                setNewSelect('');
                                setShowPlus(true);
                              } else if (event.key === 'Escape') {
                                cancel();
                              }
                            }}
                          />
                        )}
                      </GridDiv>
                    </>
                  )}
                  <Label>Default Value</Label>
                  <RowDiv>
                    <Toggle
                      selected={haveDefaultValue}
                      toggleSelected={() =>
                        setHaveDefaultValue(!haveDefaultValue)
                      }
                    />
                    <YesNo>{haveDefaultValue ? 'Yes' : 'No'}</YesNo>
                    <Input
                      disabled={!haveDefaultValue}
                      placeholder="Define the default Value"
                      {...register('defaultValue')}
                    />
                  </RowDiv>
                  <Label>Show on Onboard</Label>
                  <RowDiv>
                    <Toggle
                      selected={showOnOnboard}
                      toggleSelected={() => setShowOnOnboard(!showOnOnboard)}
                    />
                    <YesNo>{showOnOnboard ? 'Yes' : 'No'}</YesNo>
                  </RowDiv>
                  <Label>Unique Field</Label>
                  <RowDiv>
                    <Controller
                      control={control}
                      name="isUnique"
                      render={({ field }) => (
                        <>
                          <Toggle
                            selected={!!field.value}
                            toggleSelected={() => {
                              field.onChange(!field.value);
                            }}
                          />
                          <YesNo>{field.value ? 'Yes' : 'No'}</YesNo>
                        </>
                      )}
                    />
                  </RowDiv>
                  {isUniqueError && <Error>{uniqueErrMessage}</Error>}
                  <Label>Is Searchable</Label>
                  <RowDiv>
                    <Toggle
                      selected={isSearchable}
                      toggleSelected={() => toggleDefaultStatus()}
                    />
                    <YesNo>{isSearchable ? 'Yes' : 'No'}</YesNo>
                  </RowDiv>
                </ScrollArea>
                <Line className="no-margin-padding">
                  <Confirm
                    disabled={
                      !isValid ||
                      updateCustomFieldMutation.isLoading ||
                      (type === 'select' && selectValues.length === 0) ||
                      isErrorName
                    }
                  >
                    Save
                  </Confirm>
                </Line>
              </Form>
            )}

            {openDeleteDialog && (
              <DeleteDialog
                open={openDeleteDialog}
                onDelete={() => {
                  setIsSearchable(true);
                  openDeleteDialog.set(false);
                }}
                backgroundColor="#FF9900"
                buttonText="Replace Searchable Field ?"
                text="There is already one custom field set as searchable"
                onCancel={() => setIsSearchable(false)}
              />
            )}
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};

export { CustomFieldDetails };
